import React from "react";
import { motion } from "framer-motion";
import { FileText, Eye, Info, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";

export default function RatesSection() {
  const { t } = useLanguage();
  const R = t.rates;

  return (
    <section
      id="rates"
      className="bg-white px-5 py-20 md:px-8 md:py-28 dark:bg-[#080808]"
      data-testid="rates-section"
    >
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55 }}
          className="relative overflow-hidden rounded-[28px] border border-black/6 bg-gradient-to-br from-[#FDF1E8] via-white to-[#FDF1E8] p-7 shadow-[0_22px_60px_rgba(16,24,40,0.08)] md:rounded-[32px] md:p-12 dark:border-white/10 dark:bg-[linear-gradient(135deg,#101010_0%,#181818_100%)]"
        >
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#F37021]/20 blur-[120px] pointer-events-none" />

          <div className="relative grid items-center gap-8 md:gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <span className="mb-3 inline-block text-xs font-semibold uppercase tracking-[0.3em] text-[#F37021]">
                — {R.eyebrow}
              </span>
              <h2 className="mb-4 font-display text-3xl font-bold leading-[1.08] text-[#111] sm:text-4xl md:text-5xl dark:text-white">
                {R.title}
              </h2>
              <p className="max-w-lg text-base leading-relaxed text-[#555] md:text-lg dark:text-[#BAC4D4]">
                {R.subtitle}
              </p>

              <div className="mt-6 flex max-w-xl items-start gap-3 rounded-[24px] border border-black/6 bg-white p-4 shadow-[0_14px_30px_rgba(16,24,40,0.05)] dark:border-white/10 dark:bg-white/10">
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#F37021]" />
                <div>
                  <div className="font-display text-sm font-semibold text-[#111] dark:text-white">{R.noteTitle}</div>
                  <p className="mt-1 text-sm leading-relaxed text-[#555] dark:text-[#BAC4D4]">{R.noteBody}</p>
                </div>
              </div>

              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/shipping-rates"
                  className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-[#F37021] px-7 font-medium text-white shadow-[0_14px_30px_rgba(243,112,33,0.25)] transition-all hover:-translate-y-0.5 hover:bg-[#E06015]"
                  data-testid="rates-view-cta"
                >
                  <Eye className="w-5 h-5" />
                  {R.view || R.preview}
                </Link>
                <Link
                  to="/shipping-rates"
                  className="inline-flex h-14 items-center justify-center gap-2 rounded-full border border-black/10 bg-white px-7 font-medium text-[#111] transition-all hover:-translate-y-0.5 hover:bg-[#111] hover:text-white dark:border-white/10 dark:bg-white/10 dark:text-white dark:hover:bg-white dark:hover:text-[#111]"
                  data-testid="rates-open-cta"
                >
                  <ExternalLink className="w-5 h-5" />
                  {R.openLink || R.preview}
                </Link>
              </div>
            </div>

            {/* Right: Shipping Rates visual (click to open) */}
            <div className="lg:col-span-5">
              <Link
                to="/shipping-rates"
                className="group relative block aspect-[4/5] overflow-hidden rounded-[28px] border border-black/8 bg-[#111] shadow-[0_24px_60px_rgba(16,24,40,0.18)]"
                data-testid="rates-card-link"
              >
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(243,112,33,0.3),transparent_60%)]" />
                <svg className="absolute inset-0 w-full h-full opacity-[0.06]" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <pattern id="grid-rates" width="40" height="40" patternUnits="userSpaceOnUse">
                      <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid-rates)" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center p-10 text-white">
                  <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-[24px] bg-[#F37021] transition-transform group-hover:scale-[1.04]">
                    <FileText className="h-10 w-10" strokeWidth={1.5} />
                  </div>
                  <div className="text-xs tracking-[0.3em] uppercase text-[#F37021] mb-3">Interactive</div>
                  <div className="font-display text-3xl md:text-4xl font-bold text-center leading-[1.1]">
                    Shipping Rates
                  </div>
                  <div className="mt-5 text-[11px] text-white/60 tracking-wider uppercase">Click to view</div>
                </div>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
